import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';

import WebHealthCheckPage from '@pray/shared/components/WebHealthCheckPage/WebHealthCheckPage';
import eventTracking from '@pray/shared/utils/eventTracking';
import { navigator } from '@pray/shared/utils/navigation';
import storage from '@pray/shared/utils/storage';

import PageLayout from 'components/layouts/PageLayout';
import StudioPreviewPage from 'components/pages/StudioPreviewPage/StudioPreviewPage';
import useAppRoutes from 'hooks/useAppRoutes';
import IndexPage from 'pages';
import CreditsExternalPageModal from 'pages/CreditsExternalPageModal';
import YouTubeOAuthCallback from 'pages/oauth/YouTubeOAuthCallback';
import RevenueExternalPageModal from 'pages/RevenueExternalPageModal';
import WeeklyReportOpenPage from 'pages/WeeklyReportOpenPage';
import WeeklyReportUnsubscribePage from 'pages/WeeklyReportUnsubscribePage';

import { layout } from './components/layouts/constants';
import PlgOnboardingFlow from './components/pages/public/pages/get-started/PlgOnboardingFlow';
import PricingPage from './components/pages/public/pages/pricing/PricingPage';
import ProductsPage from './components/pages/public/pages/products/ProductsPage';
import { UnsavedChangesProvider } from './components/pages/StudioPage/tabs/SettingsTab/AiSettings/context/UnsavedChangesContext';
import { ExternalRedirect } from './components/router/ExternalRedirect';
import { EXTERNAL_REDIRECTS } from './constants';
import { isScenarioLayerAllowed, PREVIEW_PATH, ScenarioPreviewApp } from './dev/scenarioLayer';

const StudioPage = lazy(() => import('./components/pages/StudioPage/StudioPage'));

export default function AppRoutes() {
  const appRoutes = useAppRoutes();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = useRef<string | null>(null);
  const previousPathRef = useRef<string | null>(null);

  function trackLocationChange() {
    if (currentPathRef.current !== location.pathname) {
      if (currentPathRef.current !== null) {
        previousPathRef.current = currentPathRef.current;
      }

      currentPathRef.current = location.pathname;

      eventTracking.trackEventRoute({
        name: currentPathRef.current,
        source: previousPathRef.current,
      });

      storage.trackRouteChanged(location.pathname);

      window.scrollTo(0, 0);
      document.getElementById(layout.main)?.scrollTo(0, 0);
    }
  }

  navigator.initialize({ navigate, Link });

  useEffect(() => {
    trackLocationChange();
  }, [location]);

  return (
    <PageLayout>
      <Routes>
        {EXTERNAL_REDIRECTS.map(({ path, to }) => (
          <Route key={path} path={path} element={<ExternalRedirect to={to} />} />
        ))}
        <Route path={appRoutes.index()} element={<IndexPage />} />
        <Route
          path={appRoutes.studio()}
          element={
            <Suspense>
              {/* One unsaved-changes guard for the whole Studio shell, so the sidebar can ask before leaving. */}
              <UnsavedChangesProvider>
                <StudioPage />
              </UnsavedChangesProvider>
            </Suspense>
          }
        />
        {isScenarioLayerAllowed && (
          <Route
            path={PREVIEW_PATH}
            element={
              <Suspense>
                <ScenarioPreviewApp />
              </Suspense>
            }
          />
        )}
        <Route path={appRoutes.healthCheck()} element={<WebHealthCheckPage />} />
        <Route path={appRoutes.preview()} element={<StudioPreviewPage />} />
        <Route path={appRoutes.revenueExternalPage()} element={<RevenueExternalPageModal />} />
        <Route path={appRoutes.creditsExternalPage()} element={<CreditsExternalPageModal />} />
        <Route path={appRoutes.oauthYoutubeCallback()} element={<YouTubeOAuthCallback />} />
        <Route path={appRoutes.pricing()} element={<PricingPage />} />
        <Route path={appRoutes.products()} element={<ProductsPage />} />
        <Route path={appRoutes.getStarted()} element={<PlgOnboardingFlow />} />
        <Route path={appRoutes.weeklyReportUnsubscribe()} element={<WeeklyReportUnsubscribePage />} />
        <Route path={appRoutes.weeklyReportOpen()} element={<WeeklyReportOpenPage />} />
      </Routes>
    </PageLayout>
  );
}
